<template>
  <v-main>
    <v-container fluid>
      <v-overlay :value="isLoading" z-index="999">
        <v-progress-circular indeterminate size="64" color="primary"></v-progress-circular>
      </v-overlay>

      <v-card elevation="6" rounded="xxl" v-if="order">
        <v-card-title>
          <v-btn
            class="mx-2"
            fab
            x-small
            color="primary"
            @click="$router.back()"
          >
            <v-icon dark> mdi-arrow-left </v-icon> </v-btn
          >Edit Purchase Order
          <v-chip class="ml-3" small :color="billingStatusColor(order.billing_status)" text-color="white">
            {{ billingStatusLabels[order.billing_status] }}
          </v-chip>
          <v-chip class="ml-2" small :color="paymentStatusColor(order.payment_status)" text-color="white">
            {{ paymentStatusLabels[order.payment_status] }}
          </v-chip>
        </v-card-title>
        <v-card-text>
          <v-alert v-if="isLocked" type="info" outlined dense class="mb-4">
            This purchase order already has bills raised against it and can no longer be edited.
          </v-alert>
          <v-row>
            <v-col sm="4">
              <v-autocomplete
                v-model="form.vendor_id"
                :items="vendors"
                item-text="name"
                item-value="id"
                outlined
                :disabled="isLocked"
                prepend-inner-icon="mdi-domain"
                :error-messages="errors.vendor_id"
              >
                <template v-slot:label>Vendor <span style="color:#f44336;font-weight:bold;">*</span></template>
              </v-autocomplete>
            </v-col>
            <v-col sm="4">
              <v-menu
                v-model="poDateMenu"
                :close-on-content-click="false"
                :disabled="isLocked"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="form.po_date"
                    outlined
                    readonly
                    :disabled="isLocked"
                    prepend-inner-icon="mdi-calendar"
                    v-bind="attrs"
                    v-on="on"
                    :error-messages="errors.po_date"
                  >
                    <template v-slot:label>PO Date <span style="color:#f44336;font-weight:bold;">*</span></template>
                  </v-text-field>
                </template>
                <v-date-picker
                  v-model="form.po_date"
                  @input="poDateMenu = false"
                ></v-date-picker>
              </v-menu>
            </v-col>
            <v-col sm="4">
              <v-menu
                v-model="expectedDateMenu"
                :close-on-content-click="false"
                :disabled="isLocked"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="form.expected_delivery_date"
                    outlined
                    readonly
                    :disabled="isLocked"
                    label="Expected Delivery Date"
                    prepend-inner-icon="mdi-calendar-clock"
                    v-bind="attrs"
                    v-on="on"
                    :error-messages="errors.expected_delivery_date"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="form.expected_delivery_date"
                  @input="expectedDateMenu = false"
                ></v-date-picker>
              </v-menu>
            </v-col>
            <v-col sm="12">
              <v-textarea
                v-model="form.remarks"
                outlined
                :disabled="isLocked"
                label="Remarks"
                prepend-inner-icon="mdi-note-text"
                rows="2"
                auto-grow
                :error-messages="errors.remarks"
              ></v-textarea>
            </v-col>
          </v-row>

          <v-divider class="my-4"></v-divider>

          <!-- Add Products -->
          <template v-if="!isLocked">
            <h4 class="text-subtitle-1 font-weight-bold">Add Products</h4>
            <v-divider class="mb-4 mt-1"></v-divider>
            <v-row align="center">
              <v-col sm="2">
                <v-autocomplete
                  v-model="picker.product_id"
                  :items="products"
                  item-text="name"
                  item-value="id"
                  label="Product"
                  outlined
                  dense
                  hide-details
                  @change="onProductPick"
                ></v-autocomplete>
              </v-col>
              <v-col sm="2">
                <v-autocomplete
                  v-model="picker.variant_id"
                  :items="variantsForPicker"
                  item-text="display_name"
                  item-value="id"
                  label="Variant"
                  outlined
                  dense
                  hide-details
                  :disabled="!picker.product_id"
                  @change="onVariantPick"
                ></v-autocomplete>
              </v-col>
              <v-col sm="1">
                <v-text-field
                  v-model.number="picker.quantity"
                  type="number"
                  min="1"
                  label="Qty"
                  outlined
                  dense
                  hide-details
                ></v-text-field>
              </v-col>
              <v-col sm="2">
                <v-text-field
                  v-model.number="picker.cost_price"
                  type="number"
                  min="0"
                  label="Cost Price"
                  outlined
                  dense
                  hide-details
                  @input="applyMarkup"
                ></v-text-field>
              </v-col>
              <v-col sm="2">
                <v-text-field
                  v-model.number="picker.margin_percent"
                  type="number"
                  min="0"
                  label="Markup %"
                  outlined
                  dense
                  hide-details
                  @input="applyMarkup"
                ></v-text-field>
              </v-col>
              <v-col sm="2">
                <v-text-field
                  v-model.number="picker.selling_price"
                  type="number"
                  min="0"
                  label="Selling Price"
                  outlined
                  dense
                  hide-details
                  @input="picker.margin_percent = ''"
                ></v-text-field>
              </v-col>
              <v-col sm="1">
                <v-btn block color="primary" @click="addToOrder">
                  <v-icon small>mdi-plus</v-icon>
                </v-btn>
              </v-col>
              <v-col cols="12" v-if="picker.variant_id" class="mt-n2">
                <span class="text-caption grey--text">
                  <template v-if="pickerLastPurchasePrice !== null">Last purchase price: ₹{{ pickerLastPurchasePrice.toFixed(2) }}</template>
                  <template v-else>No previous purchase for this variant — enter what you're actually paying the vendor.</template>
                  <template v-if="pickerCurrentSellingPrice !== null"> · Current selling price: ₹{{ pickerCurrentSellingPrice.toFixed(2) }} (change it above to update it)</template>
                </span>
              </v-col>
            </v-row>
            <div class="text-caption error--text mt-1" v-if="errors.items">
              {{ Array.isArray(errors.items) ? errors.items[0] : errors.items }}
            </div>
          </template>

          <!-- Order Items -->
          <v-card outlined class="mt-6" v-if="form.items.length">
            <v-card-title class="text-subtitle-1 font-weight-bold py-3">
              Order Items ({{ form.items.length }})
            </v-card-title>
            <v-divider></v-divider>
            <div v-for="(item, index) in form.items" :key="item._key || item.id">
              <v-row align="center" class="px-4 py-2" no-gutters>
                <v-col cols="auto">
                  <v-avatar color="deep-purple lighten-4" size="32">
                    <span class="deep-purple--text font-weight-bold">{{ index + 1 }}</span>
                  </v-avatar>
                </v-col>
                <v-col class="ml-4">
                  <div class="font-weight-medium">
                    {{ item.product_name }}
                    <span v-if="item.variant_name" class="font-weight-regular grey--text"> - {{ item.variant_name }}</span>
                  </div>
                  <div class="text-caption grey--text">
                    <span v-if="item.sku">{{ item.sku }}</span>
                    <span v-if="Number(item.gst_percentage) > 0"> (+{{ Number(item.gst_percentage).toFixed(2) }}% GST)</span>
                  </div>
                </v-col>
                <v-col cols="auto" class="d-flex align-center mx-4">
                  <v-btn v-if="!isLocked" icon x-small @click="decrementQty(item)">
                    <v-icon small>mdi-minus</v-icon>
                  </v-btn>
                  <span class="mx-2">{{ item.ordered_quantity }}</span>
                  <v-btn v-if="!isLocked" icon x-small @click="incrementQty(item)">
                    <v-icon small>mdi-plus</v-icon>
                  </v-btn>
                </v-col>
                <v-col cols="auto" class="mx-4 grey--text">
                  Cost ₹{{ Number(item.purchase_price).toFixed(2) }}
                  <span v-if="item.selling_price !== null && item.selling_price !== undefined && item.selling_price !== ''">
                    · Sell ₹{{ Number(item.selling_price).toFixed(2) }}
                  </span>
                </v-col>
                <v-col cols="auto" class="mx-4 font-weight-bold primary--text">
                  ₹{{ lineTotal(item) }}
                </v-col>
                <v-col cols="auto" v-if="!isLocked">
                  <v-btn icon color="error" @click="removeItem(index)">
                    <v-icon>mdi-delete</v-icon>
                  </v-btn>
                </v-col>
              </v-row>
              <v-divider v-if="index < form.items.length - 1"></v-divider>
            </div>
            <v-divider></v-divider>
            <div class="px-4 py-3">
              <div class="d-flex justify-space-between">
                <span class="grey--text">Subtotal</span>
                <span>₹{{ totals.subtotal_amount }}</span>
              </div>
              <div class="d-flex justify-space-between">
                <span class="grey--text">GST<span v-if="totals.gst_label"> ({{ totals.gst_label }})</span></span>
                <span>₹{{ totals.gst_amount }}</span>
              </div>
              <v-divider class="my-1"></v-divider>
              <div class="d-flex justify-space-between font-weight-bold">
                <span>Total</span>
                <span class="primary--text">₹{{ totals.total_amount }}</span>
              </div>
            </div>
          </v-card>

          <v-divider class="mt-5 mb-5"> </v-divider>
          <div class="text-right">
            <v-btn text class="mr-2" @click="$router.back()">{{ isLocked ? "Back" : "Cancel" }}</v-btn>
            <v-btn
              v-if="!isLocked"
              :loading="isSaving"
              :disabled="isSaving"
              rounded
              color="primary"
              dark
              @click="save"
            >
              Update Purchase Order
            </v-btn>
          </div>
        </v-card-text>
      </v-card>
    </v-container>
  </v-main>
</template>
<script>
import axios from "axios";
export default {
  name: "PurchaseOrderEditPage",
  data() {
    return {
      order: null,
      form: {
        vendor_id: null,
        po_date: null,
        expected_delivery_date: null,
        remarks: "",
        items: [],
      },
      picker: {
        product_id: null,
        variant_id: null,
        quantity: 1,
        cost_price: 0,
        margin_percent: "",
        baseline_selling: null,
        selling_price: 0,
      },
      poDateMenu: false,
      expectedDateMenu: false,
      vendors: [],
      products: [],
      isLoading: false,
      isSaving: false,
      billingStatusLabels: {
        unbilled: "Unbilled",
        partially_billed: "Partially Billed",
        fully_billed: "Fully Billed",
      },
      paymentStatusLabels: {
        unpaid: "Unpaid",
        partial: "Partial",
        paid: "Paid",
      },
    };
  },
  computed: {
    errors() {
      return this.$store.getters["errors/getErrors"] || {};
    },
    isLocked() {
      return !!this.order && this.order.billing_status !== "unbilled";
    },
    variantsForPicker() {
      const product = this.products.find((p) => p.id === this.picker.product_id);
      return product ? product.variants || [] : [];
    },
    pickerLastPurchasePrice() {
      const variant = this.variantsForPicker.find((v) => v.id === this.picker.variant_id);
      return variant && variant.last_purchase_price !== null && variant.last_purchase_price !== undefined
        ? Number(variant.last_purchase_price)
        : null;
    },
    pickerCurrentSellingPrice() {
      const variant = this.variantsForPicker.find((v) => v.id === this.picker.variant_id);
      const current = variant ? (variant.selling_price ?? variant.price) : null;
      return current !== null && current !== undefined ? Number(current) : null;
    },
    totals() {
      let subtotal = 0;
      let gst = 0;
      const rates = new Set();
      this.form.items.forEach((item) => {
        const lineSubtotal =
          (Number(item.ordered_quantity) || 0) *
          (Number(item.purchase_price) || 0);
        const lineGst = this.roundGst(lineSubtotal * ((Number(item.gst_percentage) || 0) / 100));
        subtotal += lineSubtotal;
        gst += lineGst;
        rates.add(Number(item.gst_percentage) || 0);
      });
      let gstLabel = "";
      if (rates.size === 1) {
        gstLabel = `${[...rates][0].toFixed(2)}%`;
      } else if (rates.size > 1) {
        gstLabel = "Multiple Rates";
      }
      return {
        subtotal_amount: subtotal.toFixed(2),
        gst_amount: gst.toFixed(2),
        total_amount: (subtotal + gst).toFixed(2),
        gst_label: gstLabel,
      };
    },
  },
  mounted() {
    this.$store.dispatch("errors/setErrors", {});
    this.getMasters();
    this.getData();
  },
  methods: {
    billingStatusColor(status) {
      const colors = {
        unbilled: "grey",
        partially_billed: "orange",
        fully_billed: "blue",
      };
      return colors[status] || "grey";
    },
    paymentStatusColor(status) {
      const colors = {
        unpaid: "red",
        partial: "orange",
        paid: "teal",
      };
      return colors[status] || "grey";
    },
    async getMasters() {
      await axios
        .get(`masters?context=purchase_order`)
        .then((response) => {
          this.vendors = response.data.vendors || [];
          this.products = response.data.products_with_all_variants || [];
        })
        .catch(function (error) {
          console.log(error);
        });
    },
    onProductPick() {
      this.picker.variant_id = null;
      this.picker.cost_price = 0;
      this.picker.margin_percent = "";
      this.picker.baseline_selling = null;
      this.picker.selling_price = 0;
    },
    onVariantPick() {
      const variant = this.variantsForPicker.find((v) => v.id === this.picker.variant_id);
      // Default cost from what was actually paid last time (if known) - never
      // from the variant's selling price, which would otherwise silently
      // become the recorded cost on every purchase. Selling price defaults
      // to what the variant currently sells for (discount applied) and is
      // remembered as the baseline, so addToOrder only sends a selling price
      // when it was deliberately changed - sending it back unchanged would
      // make the backend clear the variant's discount.
      this.picker.cost_price = variant && variant.last_purchase_price !== null && variant.last_purchase_price !== undefined
        ? Number(variant.last_purchase_price)
        : 0;
      this.picker.margin_percent = "";
      this.picker.selling_price = variant ? Number(variant.selling_price ?? variant.price) || 0 : 0;
      this.picker.baseline_selling = this.picker.selling_price;
    },
    // Selling Price = Cost Price + Markup % of Cost Price (200 at 50% -> 300).
    // Does nothing until both are filled, so a hand-typed price is never
    // overwritten; typing in Selling Price clears the markup instead.
    applyMarkup() {
      const cost = Number(this.picker.cost_price);
      const pct = this.picker.margin_percent;
      if (!(cost > 0) || pct === "" || pct === null || isNaN(Number(pct))) return;
      this.picker.selling_price = Math.round(cost * (1 + Number(pct) / 100) * 100) / 100;
    },
    addToOrder() {
      const product = this.products.find((p) => p.id === this.picker.product_id);
      const variant = this.variantsForPicker.find((v) => v.id === this.picker.variant_id);
      if (!product || !variant) {
        return;
      }

      const quantity = Number(this.picker.quantity) || 1;
      const costPrice = Number(this.picker.cost_price) || 0;
      // null = leave the variant's price (and discount) untouched
      const sellingPrice = Number(this.picker.selling_price) === Number(this.picker.baseline_selling)
        ? null
        : Number(this.picker.selling_price) || 0;
      const existing = this.form.items.find((i) => i.variant_id === variant.id);

      if (existing) {
        existing.ordered_quantity += quantity;
      } else {
        this.form.items.push({
          _key: `${product.id}_${variant.id}`,
          product_id: product.id,
          variant_id: variant.id,
          product_name: product.name,
          variant_name: variant.display_name,
          sku: product.sku,
          gst_percentage: product.gst_percentage,
          ordered_quantity: quantity,
          purchase_price: costPrice,
          selling_price: sellingPrice,
        });
      }

      this.picker = { product_id: null, variant_id: null, quantity: 1, cost_price: 0, margin_percent: "", baseline_selling: null, selling_price: 0 };
    },
    incrementQty(item) {
      item.ordered_quantity += 1;
    },
    decrementQty(item) {
      if (item.ordered_quantity > 1) {
        item.ordered_quantity -= 1;
      }
    },
    removeItem(index) {
      this.form.items.splice(index, 1);
    },
    lineTotal(item) {
      const subtotal =
        (Number(item.ordered_quantity) || 0) * (Number(item.purchase_price) || 0);
      const gst = this.roundGst(subtotal * ((Number(item.gst_percentage) || 0) / 100));
      return (subtotal + gst).toFixed(2);
    },
    // GST rounds to the nearest rupee, tying DOWN at exactly X.50 (unlike
    // standard rounding) - e.g. 99.50 -> 99, 99.51 -> 100, 99.99 -> 100.
    roundGst(value) {
      return Math.ceil(value - 0.5);
    },
    async getData() {
      this.isLoading = true;
      let encryptedId = this.$route.params.id;
      await axios
        .get(`/purchase_orders/${encryptedId}`)
        .then((response) => {
          this.order = response.data.data;
          this.form = {
            vendor_id: this.order.vendor_id,
            po_date: this.order.po_date,
            expected_delivery_date: this.order.expected_delivery_date,
            remarks: this.order.remarks,
            items: (this.order.items || []).map((item) => ({
              id: item.id,
              product_id: item.product_id,
              variant_id: item.variant_id,
              product_name: item.product ? item.product.name : "",
              variant_name: item.productVariant
                ? ((item.productVariant.color || "") + (item.productVariant.size ? " - " + item.productVariant.size : "")).trim()
                : "",
              sku: item.product ? item.product.hsn_code : "",
              ordered_quantity: item.ordered_quantity,
              purchase_price: item.purchase_price,
              // Not re-applied on save unless the seller deliberately edits it
              // here - null items are skipped by the backend's price update.
              selling_price: null,
              gst_percentage: item.gst_percentage,
            })),
          };
        })
        .catch((error) => {
          console.error("Error fetching data:", error);
        });
      this.isLoading = false;
    },
    async save() {
      try {
        this.isSaving = true;
        this.$store.dispatch("errors/setErrors", {});
        const payload = {
          ...this.form,
          items: this.form.items.map((item) => ({
            product_id: item.product_id,
            variant_id: item.variant_id,
            ordered_quantity: item.ordered_quantity,
            purchase_price: item.purchase_price,
            selling_price: item.selling_price,
            gst_percentage: item.gst_percentage,
          })),
        };
        await axios.patch(`/purchase_orders/${this.$route.params.id}`, payload);
        this.isSaving = false;
        this.$router.push("/purchase-orders");
      } catch (e) {
        console.log(e);
        this.isSaving = false;
        if (e.response && e.response.data && e.response.data.errors) {
          this.$store.dispatch("errors/setErrors", e.response.data.errors);
        }
      }
    },
  },
};
</script>
